import type { CollectionChildren, Node } from "@react-types/shared";
import { clsx } from "clsx";
import { cloneElement, type CSSProperties, type ReactElement, type ReactNode, type RefObject, useRef } from "react";
import {
  type AriaListBoxOptions,
  useListBox,
  useListBoxSection,
  useOption,
  useSeparator,
  VisuallyHidden,
} from "react-aria";
import { type ListState, useListState } from "react-stately";

import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import { Badge } from "../Badge/Badge.tsx";
import styleMenu from "../ContextualMenu/ContextualMenu.module.css";
import { Label } from "../Label/Label.tsx";
import { HStack } from "../Stack/HStack.tsx";

export interface ListBoxProps<T> extends AriaListBoxOptions<T> {
  state: ListState<T>;
  listBoxRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  style?: CSSProperties;
  showLabel?: boolean;
}

export const ListBox = <T extends object>(props: ListBoxProps<T>): ReactNode => {
  const ref = useRef<HTMLDivElement>(null);
  const { listBoxRef = ref, state, ...options } = props;
  const { listBoxProps, labelProps } = useListBox<T>(options, state, listBoxRef);

  const dataAttributes = useDataAttributes(props);
  const testId = `${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}listbox`;

  return (
    <>
      {props.label != undefined &&
        (props.showLabel ? (
          <HStack>
            <Label {...labelProps}>{props.label}</Label>
            <Badge label={state.selectionManager.selectedKeys.size} inline />
            {/* <Spacer /> */}
            {/* {props.isReadOnly && <div className={formStyle.read_only_tag}>read only</div>}
          {!props.isRequired && !props.isReadOnly && <div className={formStyle.optional_tag}>optional</div>} */}
          </HStack>
        ) : (
          <VisuallyHidden>
            <label {...labelProps}>{props.label}</label>
          </VisuallyHidden>
        ))}
      <div
        {...listBoxProps}
        className={clsx(styleMenu.list, props.className)}
        ref={listBoxRef}
        style={props.style}
        {...dataAttributes}
        data-testid={testId}
      >
        {[...state.collection].map((item) =>
          item.type === "section" ? (
            <SelectSection key={`${item.key}-section`} section={item} state={state} />
          ) : (
            <SelectOption key={item.key} item={item} state={state} />
          ),
        )}
      </div>
    </>
  );
};

interface SelectSectionProps<T> {
  section: Node<T>;
  state: ListState<T>;
}

const SelectSection = <T extends object>({ section, state }: SelectSectionProps<T>): ReactNode => {
  const { itemProps, headingProps, groupProps } = useListBoxSection({
    heading: section.rendered,
    "aria-label": section["aria-label"],
  });

  const { separatorProps } = useSeparator({ elementType: "div" });

  return (
    <>
      {section.key !== state.collection.getFirstKey() && (
        <div
          role="presentation"
          {...separatorProps}
          className={styleMenu.separator}
          style={{ margin: "3px 0", flex: "0 0 auto" }}
        />
      )}
      <div {...itemProps} style={{ flex: "0 0 auto" }} data-testid="combobox-menu-section">
        {section.rendered != undefined && (
          <Label {...headingProps} className={clsx(styleMenu.category_label, headingProps.className)}>
            {section.rendered}
          </Label>
        )}
        <div {...groupProps}>
          {[...(state.collection.getChildren?.(section.key) ?? [...section.childNodes])].map((node) => (
            <SelectOption key={node.key} item={node} state={state} />
          ))}
        </div>
      </div>
    </>
  );
};

/** Extends the Option with that interface */
export interface CollectionItem {
  isFocused?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
}

interface SelectOptionProps<T> {
  state: ListState<T>;
  item: Node<T>;
}

// Add onAction, onClose?
const SelectOption = <T extends object>({ item, state }: SelectOptionProps<T>): ReactNode => {
  const ref = useRef<HTMLDivElement>(null);
  const { optionProps, isFocused, isDisabled, isSelected } = useOption<T>({ key: item.key }, state, ref);

  // Pass all data-* attributes, used for testing purposes
  const dataAttributesProps: Record<string, string> = {};
  for (const key of Object.keys(item.props as object)) {
    if (key.startsWith("data-")) {
      dataAttributesProps[key] = (item.props as Record<string, string>)[key];
    }
  }

  return (
    <div
      {...optionProps}
      className={clsx(styleMenu.item_container, { [styleMenu.item_focus]: isFocused })}
      ref={ref}
      data-testid="combobox-menu-optionitem"
    >
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {cloneElement(item.rendered as ReactElement<any>, { isFocused, isDisabled, isSelected })}
    </div>
  );
};

export const ListBoxWithChildren = <T extends object>(
  props: Omit<ListBoxProps<T>, "state"> & { children: CollectionChildren<T> },
): ReactNode => {
  const state = useListState(props);

  return <ListBox {...props} state={state} />;
};
